import React from 'react'
import { useIntl } from 'react-intl'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Box, Button, Checkbox, LinearProgress } from '@mui/material'
import { H3 } from '../../../layouts/TextStyles'
import { ui, tone } from '../../../utils/uiTokens'
import { money, SALARY_DEFAULTS } from '../../../utils/salaryFormulas'
import { dateRange, ddmm } from '../../../utils/periodDates'
import { useAppSelector } from '../../../app/hooks'
import { SUPER_ADMIN } from '../../../utils/constants'
import { directorApi, SalaryDecision } from '../../../features/director/directorApi'
import SalaryDetailView from '../../admin/mentorSalaryPage/components/SalaryDetailView'
import SalaryFactorRules from '../../admin/mentorSalaryPage/components/SalaryFactorRules'
import { Pill, btn, muted, page, sub } from '../ui'
import { SurveyInput } from './DirectorSalaryPage'

const WEEKS = [0, 1, 2, 3, 4]

// Толық жалақы беті ментора у сервис басшысы: опрос, бекіту / ашу, жалақы берілді
const DirectorSalaryDetailPage = () => {
    const { formatMessage } = useIntl()
    const navigate = useNavigate()
    const { mentorUserId } = useParams()
    const [params, setParams] = useSearchParams()
    const mentorId = Number(mentorUserId)
    const courseSubjectId = Number(params.get('courseSubjectId') || 0)
    const cycleNumber = Number(params.get('cycleNumber') || 1)
    const weekNumber = Number(params.get('weekNumber') || 0)
    const groupId = Number(params.get('groupId') || 0)
    const isWeek = weekNumber > 0
    const isSuper = useAppSelector((s) => s.user.userInfo.roles.includes(SUPER_ADMIN))

    const { data, isFetching } = directorApi.useGetSalaryDetailQuery({ mentorId, courseSubjectId, monthNumber: cycleNumber, weekNumber, groupId })
    const [decide, { isLoading: deciding }] = directorApi.useDecideMutation()

    const approved = data?.approvalStatus === 'APPROVED'
    const name = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim() || `#${mentorId}`
    const surveyRow = {
        mentorUserId: mentorId,
        courseSubjectId,
        surveyPercent: data?.surveyPercent,
        paid: data?.paid,
    } as any

    const act = async (decision: SalaryDecision) => {
        try {
            await decide({ mentorUserId: mentorId, courseSubjectId, cycleNumber, decision }).unwrap()
            toast.success(formatMessage({ id: `dirDecision_${decision}` }))
        } catch { /* общий обработчик */ }
    }

    const setWeek = (w: number) => {
        const next = new URLSearchParams(params)
        next.set('weekNumber', String(w))
        setParams(next, { replace: true })
    }

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <Button onClick={() => navigate('/director/salary')} sx={{ color: ui.sub, paddingLeft: 0 }}>
                    ← {formatMessage({ id: 'dirAllMentors' })}
                </Button>
                <H3>{formatMessage({ id: 'dirMentorSalaryTitle' }, { name })}</H3>
                <Box sx={{ ...sub, margin: '4px 0 14px' }}>
                    {[data?.subjectTitleKz || data?.subjectTitleRu, `${cycleNumber} ${formatMessage({ id: 'month' }).toLowerCase()}`].filter(Boolean).join(' · ')}
                    {data?.periodStart && ` · ${dateRange(data.periodStart, data.periodEnd)}`}
                    {data?.payFrom && ` · ${formatMessage({ id: 'dirPayWindow' })} ${dateRange(data.payFrom, data.payTo)}`}
                </Box>

                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', marginBottom: '14px' }}>
                    {WEEKS.map((w) => (
                        <Box key={w} sx={weekTab(w === weekNumber)} onClick={() => setWeek(w)}>
                            {w ? `${w} ${formatMessage({ id: 'week' })}` : formatMessage({ id: 'allWeeks' })}
                        </Box>
                    ))}
                </Box>

                {isFetching || !data ? (
                    <LinearProgress />
                ) : (
                    <>
                        {data.paid && (
                            <Box sx={{ ...banner, background: tone.ok.bg, color: tone.ok.text, border: `1px solid ${tone.ok.border}` }}>
                                ✅ <b>{formatMessage({ id: 'dirPaidBanner' }, { date: ddmm(data.paidAt) })}</b> · {money(data.total)}
                            </Box>
                        )}
                        {approved && (
                            <Box sx={{ ...banner, background: tone.ok.bg, color: tone.ok.text, border: `1px solid ${tone.ok.border}` }}>
                                🔒 <b>{formatMessage({ id: 'approvalApproved' })} {ddmm(data.approvalDecidedAt)}</b> · {formatMessage({ id: 'dirFrozenNote' })}
                            </Box>
                        )}

                        <Box sx={surveyBox}>
                            <Box>
                                <Box sx={{ fontWeight: 700, color: ui.ink }}>{formatMessage({ id: 'salarySurveyLabel' })}</Box>
                                <Box sx={muted}>{formatMessage({ id: 'dirSurveyHint' })}</Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SurveyInput row={surveyRow} cycleNumber={cycleNumber} wide />
                                <b style={{ color: ui.pink }}>%</b>
                            </Box>
                        </Box>

                        {!isWeek && (
                            <Box sx={{ ...surveyBox, marginTop: '10px' }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <b>{formatMessage({ id: 'approvalCol' })}:</b>
                                    {approved ? (
                                        <Pill tone="good">🔒 {formatMessage({ id: 'approvalApproved' })}</Pill>
                                    ) : (
                                        <Pill tone="warn">{formatMessage({ id: 'approvalPending' })}</Pill>
                                    )}
                                    {data.paid && <Pill tone="good">{formatMessage({ id: 'dirPaidShort' })} {ddmm(data.paidAt)}</Pill>}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                    {!approved && (
                                        <Button
                                            variant="contained"
                                            sx={{ ...btn, background: ui.pink, '&:hover': { background: ui.pinkDark } }}
                                            disabled={deciding}
                                            onClick={() => act('APPROVE')}
                                        >
                                            {formatMessage({ id: 'approvalApprove' })}
                                        </Button>
                                    )}
                                    {approved && !data.paid && (
                                        <Button variant="outlined" sx={btn} disabled={deciding} onClick={() => act('UNAPPROVE')}>
                                            {formatMessage({ id: 'dirUnapprove' })}
                                        </Button>
                                    )}
                                    {approved && (
                                        <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', fontWeight: 700, fontSize: '13px' }}>
                                            <Checkbox
                                                size="small"
                                                checked={!!data.paid}
                                                disabled={deciding || (!!data.paid && !isSuper)}
                                                onChange={(e) => act(e.target.checked ? 'PAID' : 'UNPAID')}
                                            />
                                            {formatMessage({ id: 'dirPaidCol' })}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}

                        {isWeek && <Box sx={{ ...muted, marginTop: '10px' }}>{formatMessage({ id: 'salaryWeekNote' })}</Box>}

                        <Box sx={{ marginTop: '18px' }}>
                            <SalaryDetailView detail={data} editable={false} weekMode={isWeek} weekNumber={weekNumber} />
                        </Box>

                        <Box sx={{ marginTop: '24px' }}>
                            <H3>{formatMessage({ id: 'salaryRules' })}</H3>
                            <Box sx={{ marginTop: '12px' }}>
                                <SalaryFactorRules config={SALARY_DEFAULTS} />
                            </Box>
                        </Box>
                    </>
                )}
            </Box>
        </Box>
    )
}

const banner = { borderRadius: '12px', padding: '12px 16px', marginBottom: '10px', fontSize: '13.5px' }

const surveyBox = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    border: `1px solid ${ui.border}`,
    borderRadius: ui.radius,
    padding: '14px 18px',
    backgroundColor: ui.surface,
}

const weekTab = (active: boolean) => ({
    padding: '8px 14px',
    borderRadius: ui.radius,
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: '13px',
    fontWeight: active ? 700 : 500,
    color: active ? ui.pink : ui.sub,
    backgroundColor: active ? ui.pinkSoft : 'transparent',
    border: `1px solid ${active ? 'transparent' : ui.border}`,
})

export default DirectorSalaryDetailPage
